import React, { useState, useEffect, useRef } from "react";
import { IoCloseOutline } from "react-icons/io5";
import Select from "react-select";
import axios from "axios";
import API_URL from "../../../src/Config";
import "./CylinderPopup.css";
import { FiUpload } from "react-icons/fi";
import { MdAttachFile } from "react-icons/md";
import { MdDeleteOutline } from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

const statusOptions = [
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "In Progress", value: "in progress" }, // Fixed: added space
  { label: "Failed", value: "failed" },
];

const CylinderPopup = ({
  closePopup,
  handleNewDepartment,
  detectorId,
  scheduleId,
}) => {
  console.log("🧪 detectorId passed to popup:", detectorId);
  console.log("📅 scheduleId passed to popup:", scheduleId);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [remark, setRemark] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPANFiles, setSelectedPANFiles] = useState([]);
  const [passFail, setPassFail] = useState("");
  const [isPANPopupOpen, setIsPANPopupOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const panFileInputRef = useRef(null);
  const [serviceDueDate, setServiceDueDate] = useState(new Date());
  const today = new Date();

  const handleStatusChange = (option) => {
    setSelectedStatus(option);
    setRemark("");
    setPassFail("");
    setSelectedPANFiles([]);
    setServiceDueDate(new Date());
    setErrors({});
  };

  const handleSaveClick = async () => {
    console.log("🔵 handleSaveClick triggered");

    const newErrors = {};

    // Validate Status
    if (!selectedStatus) {
      newErrors.status = "Status is required.";
      console.log("❌ Status validation failed");
    }

    const status = selectedStatus?.value?.toLowerCase();
    const isCompleted = status === "completed";
    const isCancelled = status === "cancelled";
    const isFailed = status === "failed";
    const isInProgress = status === "in progress";

    console.log("📊 Status:", status);
    console.log("✅ isCompleted:", isCompleted);
    console.log("✅ isCancelled:", isCancelled);
    console.log("✅ isFailed:", isFailed);
    console.log("✅ isInProgress:", isInProgress);

    // Service Date is REQUIRED for ALL statuses (per API)
    if (!serviceDueDate) {
      newErrors.serviceDueDate = "Service Date is required";
      console.log("❌ Service date validation failed");
    }

    // Validate Notes - REQUIRED for completed, failed, cancelled
    if ((isCompleted || isFailed || isCancelled) && remark.trim() === "") {
      newErrors.remark = `Remark is required for ${status} status.`;
      console.log("❌ Remark validation failed");
    }

    // Validate Pass/Fail - REQUIRED for Completed
    if (isCompleted && !passFail) {
      newErrors.passFail = "Please select Pass or Fail.";
      console.log("❌ Pass/Fail validation failed");
    }

    // Attachments REQUIRED for Completed or Failed
    if ((isCompleted || isFailed) && selectedPANFiles.length === 0) {
      newErrors.attachments = "Attachment is required for this status.";
      console.log("❌ Attachments validation failed");
    }

    // If any errors, set them and stop submission
    if (Object.keys(newErrors).length > 0) {
      console.log("❌ Validation errors:", newErrors);
      setErrors(newErrors);
      return;
    }

    console.log("✅ All validations passed, proceeding with API call");

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");

      const formData = new FormData();
      
      // Status (REQUIRED)
      formData.append("status", status);
      console.log("📤 Appending status:", status);

      // Service Date (REQUIRED for all statuses)
      const formattedDate = format(new Date(serviceDueDate), "yyyy-MM-dd");
      formData.append("service_date", formattedDate);
      console.log("📤 Appending service_date:", formattedDate);

      // Notes (REQUIRED for completed, failed, cancelled)
      if (isCompleted || isFailed || isCancelled) {
        formData.append("notes", remark.trim());
        console.log("📤 Appending notes:", remark.trim());
      }

      // Pass/Fail (REQUIRED for completed)
      if (isCompleted && passFail) {
        formData.append("pass_fail", passFail.toLowerCase());
        console.log("📤 Appending pass_fail:", passFail.toLowerCase());
      }

      // Attachments (REQUIRED for completed and failed)
      if (selectedPANFiles.length > 0) {
        selectedPANFiles.forEach((file, index) => {
          formData.append("attachments", file);
          console.log(`📤 Appending file ${index + 1}:`, file.name);
        });
      }

      console.log(
        "🚀 Making API call to:",
        `${API_URL}/api/update_calibration_schedule/${scheduleId}`
      );

      // Log FormData contents
      console.log("📋 FormData contents:");
      for (let pair of formData.entries()) {
        console.log(pair[0] + ": ", pair[1]);
      }

      const response = await axios.put(
        `${API_URL}/api/update_calibration_schedule/${scheduleId}`,
        formData,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("✅ API Response:", response.data);

      if (response.status === 200) {
        console.log("✅ Update successful, refreshing data");
        handleNewDepartment();
        closePopup();
      }
    } catch (err) {
      console.error("❌ Save failed:", err);
      console.error("❌ Error response:", err.response?.data);
      console.error("❌ Error message:", err.message);

      // Show error to user
      setErrors({
        general:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to update status. Please try again.",
      });
    } finally {
      setIsLoading(false);
      console.log("🔵 handleSaveClick completed");
    }
  };

  const isCancelled = selectedStatus?.value === "cancelled";
  const isInProgress = selectedStatus?.value === "in progress"; // Fixed: added space
  const isCompleted = selectedStatus?.value === "completed";
  const isFailed = selectedStatus?.value === "failed";

  const handleUploadButtonClick = (inputRef) => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileInputChange = (files, setFiles) => {
    const fileArray = Array.from(files);
    setFiles(fileArray);
    console.log("📁 Files selected:", fileArray.length);
  };

  const handlePANIconClick = () => {
    setIsPANPopupOpen(true);
  };

  const handlePANCloseAttachment = () => {
    setIsPANPopupOpen(false);
  };

  const handleDeletePanFile = (indexToDelete) => {
    console.log("Delete function called for index:", indexToDelete);
    const updatedFiles = selectedPANFiles.filter(
      (_, index) => index !== indexToDelete
    );
    setSelectedPANFiles(updatedFiles);

    if (updatedFiles.length === 0) {
      handlePANCloseAttachment();
    }
  };

  return (
    <div className="reminder-popup-model" ref={popupRef}>
      <div className="reminder-popup-content gas-detector-popuup">
        <div className="reminder-popup-model-header">
          <div className="reminder-popup-model-content-header">
            <div className="reminder-popup-model-content-header-left-part">
              Calibration Status Update
            </div>
            <div className="reminder-popup-model-content-header-right-part"></div>
          </div>

          <div className="popup-new-form">
            <form
              className="popup-form-detail"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="new-data-form">
                {/* Display general error if any */}
                {errors.general && (
                  <div className="form-errrs">
                    <p className="error-msg error-msg-general">
                      {errors.general}
                    </p>
                  </div>
                )}

                {/* Status Field - REQUIRED for all */}
                <div className="form-group-select">
                  <label>
                    Status <span className="text-danger">*</span>
                  </label>
                  <Select
                    className="popup-select"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    options={statusOptions}
                    placeholder="Select status"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
                <div className="form-errrs">
                  <label className="err-label"></label>
                  {errors.status && (
                    <p className="error-msg">{errors.status}</p>
                  )}
                </div>

                {/* Service Date - REQUIRED for ALL statuses */}
                <div className="form-group">
                  <label>
                    Service Date<span className="text-danger">*</span>
                  </label>
                  <div className="new_cylinder_popup_input input-group">
                    <DatePicker
                      selected={serviceDueDate}
                      onChange={(date) => {
                        setServiceDueDate(date);
                        setErrors((prevErrors) => {
                          const updatedErrors = { ...prevErrors };
                          delete updatedErrors.serviceDueDate;
                          return updatedErrors;
                        });
                      }}
                      placeholderText="Select Service date"
                      dateFormat="dd/MM/yyyy"
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      maxDate={today}
                    />
                  </div>
                </div>

                <div className="form-errrs">
                  <label className="err-label"></label>
                  {errors.serviceDueDate && (
                    <p className="error-msg">{errors.serviceDueDate}</p>
                  )}
                </div>

                {/* COMPLETED Status Fields */}
                {isCompleted && (
                  <>
                    {/* Pass/Fail - REQUIRED */}
                    <div className="form-group">
                      <label>
                        Result <span className="text-danger">*</span>
                      </label>
                      <div className="radio-group-cylinder">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="result"
                            value="pass"
                            checked={passFail === "pass"}
                            onChange={(e) => {
                              setPassFail(e.target.value);
                              setErrors((prevErrors) => {
                                const updatedErrors = { ...prevErrors };
                                delete updatedErrors.passFail;
                                return updatedErrors;
                              });
                            }}
                          />
                          Pass
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="result"
                            value="fail"
                            checked={passFail === "fail"}
                            onChange={(e) => {
                              setPassFail(e.target.value);
                              setErrors((prevErrors) => {
                                const updatedErrors = { ...prevErrors };
                                delete updatedErrors.passFail;
                                return updatedErrors;
                              });
                            }}
                          />
                          Fail
                        </label>
                      </div>
                    </div>

                    <div className="form-errrs">
                      <label className="err-label"></label>
                      {errors.passFail && (
                        <p className="error-msg">{errors.passFail}</p>
                      )}
                    </div>

                    {/* Attachments - REQUIRED */}
                    <div className="form-group-attachments">
                      <div className="form-group">
                        <label htmlFor="pan_attachments">
                          Upload File<span className="text-danger">*</span>
                        </label>
                        <button
                          type="button"
                          className="challan-btn"
                          onClick={() =>
                            handleUploadButtonClick(panFileInputRef)
                          }
                          ref={buttonRef}
                        >
                          <FiUpload /> Upload File
                        </button>
                        <input
                          id="pan_attachments"
                          type="file"
                          ref={panFileInputRef}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const files = e.target.files;
                            handleFileInputChange(files, setSelectedPANFiles);
                            if (files && files.length > 0) {
                              setErrors((prevErrors) => {
                                const updatedErrors = { ...prevErrors };
                                delete updatedErrors.attachments;
                                return updatedErrors;
                              });
                            }
                          }}
                          accept="*/*"
                          multiple
                        />

                        {selectedPANFiles.length > 0 && (
                          <span
                            className="inquiry-file-btn-cylinder"
                            onClick={handlePANIconClick}
                          >
                            <MdAttachFile className="inquiry-file-icon" />{" "}
                            {`${selectedPANFiles.length}`}
                          </span>
                        )}

                        {isPANPopupOpen && (
                          <div className="attach-popup-amc2">
                            <div className="attach-popup-contant">
                              <div className="attachment-header">
                                <div className="attachment-header-content">
                                  Attachments
                                </div>
                                <button
                                  type="button"
                                  className="fiile-close-btn"
                                  onClick={handlePANCloseAttachment}
                                >
                                  <IoCloseOutline className="file-close-icon" />
                                </button>
                              </div>
                              {selectedPANFiles.length > 0 && (
                                <div className="attach-scrroll-body">
                                  {selectedPANFiles.map((file, index) => (
                                    <div
                                      className="attach-popup-contant-body"
                                      key={index}
                                    >
                                      <div className="attach-file-container">
                                        <div className="attach-file-show">
                                          <div className="text-attachment">
                                            {file.name}
                                          </div>
                                          <div className="text-attachment-file">
                                            File Size: (
                                            {(file.size / 1024).toFixed(2)} KB)
                                          </div>
                                        </div>
                                        <div
                                          className="file-delete-btn"
                                          onClick={() =>
                                            handleDeletePanFile(index)
                                          }
                                        >
                                          <span>
                                            <MdDeleteOutline className="file-delete-icon" />
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-errrs">
                      <label className="err-label"></label>
                      {errors.attachments && (
                        <p className="error-msg">{errors.attachments}</p>
                      )}
                    </div>

                    {/* Remark - REQUIRED for completed */}
                    <div className="form-group">
                      <label>
                        Remark <span className="text-danger">*</span>
                      </label>
                      <div className="new_cylinder_popup_input input-group">
                        <textarea
                          value={remark}
                          onChange={(e) => {
                            setRemark(e.target.value);
                            setErrors((prevErrors) => {
                              const updatedErrors = { ...prevErrors };
                              delete updatedErrors.remark;
                              return updatedErrors;
                            });
                          }}
                          placeholder="Enter remark"
                          rows={3}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-errrs">
                      <label className="err-label"></label>
                      {errors.remark && (
                        <p className="error-msg">{errors.remark}</p>
                      )}
                    </div>
                  </>
                )}

                {/* FAILED Status Fields */}
                {isFailed && (
                  <>
                    {/* Attachments - REQUIRED */}
                    <div className="form-group-attachments">
                      <div className="form-group">
                        <label htmlFor="pan_attachments">
                          Upload File<span className="text-danger">*</span>
                        </label>
                        <button
                          type="button"
                          className="challan-btn"
                          onClick={() =>
                            handleUploadButtonClick(panFileInputRef)
                          }
                          ref={buttonRef}
                        >
                          <FiUpload /> Upload File
                        </button>
                        <input
                          id="pan_attachments"
                          type="file"
                          ref={panFileInputRef}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const files = e.target.files;
                            handleFileInputChange(files, setSelectedPANFiles);
                            if (files && files.length > 0) {
                              setErrors((prevErrors) => {
                                const updatedErrors = { ...prevErrors };
                                delete updatedErrors.attachments;
                                return updatedErrors;
                              });
                            }
                          }}
                          accept="*/*"
                          multiple
                        />

                        {selectedPANFiles.length > 0 && (
                          <span
                            className="inquiry-file-btn-cylinder"
                            onClick={handlePANIconClick}
                          >
                            <MdAttachFile className="inquiry-file-icon" />{" "}
                            {`${selectedPANFiles.length}`}
                          </span>
                        )}

                        {isPANPopupOpen && (
                          <div className="attach-popup-amc2">
                            <div className="attach-popup-contant">
                              <div className="attachment-header">
                                <div className="attachment-header-content">
                                  Attachments
                                </div>
                                <button
                                  type="button"
                                  className="fiile-close-btn"
                                  onClick={handlePANCloseAttachment}
                                >
                                  <IoCloseOutline className="file-close-icon" />
                                </button>
                              </div>
                              {selectedPANFiles.length > 0 && (
                                <div className="attach-scrroll-body">
                                  {selectedPANFiles.map((file, index) => (
                                    <div
                                      className="attach-popup-contant-body"
                                      key={index}
                                    >
                                      <div className="attach-file-container">
                                        <div className="attach-file-show">
                                          <div className="text-attachment">
                                            {file.name}
                                          </div>
                                          <div className="text-attachment-file">
                                            File Size: (
                                            {(file.size / 1024).toFixed(2)} KB)
                                          </div>
                                        </div>
                                        <div
                                          className="file-delete-btn"
                                          onClick={() =>
                                            handleDeletePanFile(index)
                                          }
                                        >
                                          <MdDeleteOutline className="file-delete-icon" />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-errrs">
                      <label className="err-label"></label>
                      {errors.attachments && (
                        <p className="error-msg">{errors.attachments}</p>
                      )}
                    </div>

                    {/* Remark - REQUIRED for failed */}
                    <div className="form-group">
                      <label>
                        Remark <span className="text-danger">*</span>
                      </label>
                      <div className="new_cylinder_popup_input input-group">
                        <textarea
                          value={remark}
                          onChange={(e) => {
                            setRemark(e.target.value);
                            setErrors((prevErrors) => {
                              const updatedErrors = { ...prevErrors };
                              delete updatedErrors.remark;
                              return updatedErrors;
                            });
                          }}
                          placeholder="Enter remark"
                          rows={3}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-errrs">
                      <label className="err-label"></label>
                      {errors.remark && (
                        <p className="error-msg">{errors.remark}</p>
                      )}
                    </div>
                  </>
                )}

                {/* CANCELLED Status Fields */}
                {isCancelled && (
                  <>
                    {/* Remark - REQUIRED for cancelled */}
                    <div className="form-group">
                      <label>
                        Remark <span className="text-danger">*</span>
                      </label>
                      <div className="new_cylinder_popup_input input-group">
                        <textarea
                          value={remark}
                          onChange={(e) => {
                            setRemark(e.target.value);
                            setErrors((prevErrors) => {
                              const updatedErrors = { ...prevErrors };
                              delete updatedErrors.remark;
                              return updatedErrors;
                            });
                          }}
                          placeholder="Enter remark"
                          rows={3}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-errrs">
                      <label className="err-label"></label>
                      {errors.remark && (
                        <p className="error-msg">{errors.remark}</p>
                      )}
                    </div>
                  </>
                )}

                {/* IN PROGRESS Status Fields */}
                {isInProgress && (
                  <>
                    {/* Remark - OPTIONAL for in progress */}
                    <div className="form-group">
                      <label>Remark</label>
                      <div className="new_cylinder_popup_input input-group">
                        <textarea
                          value={remark}
                          onChange={(e) => {
                            setRemark(e.target.value);
                          }}
                          placeholder="Enter remark (optional)"
                          rows={3}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>

          <div className="footers">
            <div className="button-group">
              <button
                className="person-user-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Update"}
              </button>
              <button
                className="person-user-cancel-button"
                type="button"
                onClick={closePopup}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CylinderPopup;